/* eslint-disable import/prefer-default-export */
/* eslint-disable no-underscore-dangle */
import { TRANSITION_TIMEOUT_TIME } from '../../Data/Constants';
import { getEnvConfigForFormId } from '../__helpers__/component.helpers';
import { updateEnterPhase } from '../../Lib/redux/slices/transition';
import {
  bounceInDownCss,
  bounceInUpCss,
  bounceOutDownCss,
  bounceOutUpCss,
  backwardDirectionBounceCSS,
  forwardDirectionBounceCSS,
  customExitBounceOutUpCss,
} from './bounceTransitionCss';

/**
 * @param {stylesToApply} string i.e. CSS that needs to be applied
 * @param {styleSheetId} string id of the existing stylesheet in the document head
 *
 * This function adds or replaces the stylesheet of the provided ID with the styles provided
 */
const applyStyleSheet = ({ stylesToApply, styleSheetId }) => {
  const head =
    document.head || document.getElementsByTagName('head')
      ? document.head || document.getElementsByTagName('head')[0]
      : undefined;

  if (head) {
    const currentStyleSheet = document.getElementById(styleSheetId);

    const styleSheet = currentStyleSheet || document.createElement('style');

    styleSheet.id = styleSheetId;
    styleSheet.type = 'text/css';

    if (currentStyleSheet) {
      // overwrite pre-existing style
      document.getElementById(styleSheetId).innerHTML = stylesToApply;
    } else {
      // add a style if the stylesheet doesn't exist
      styleSheet.appendChild(document.createTextNode(stylesToApply));
      head.appendChild(styleSheet);
    }
  }
};

// /**
//  * isThereConfigForFormId
//  * @param {*} config: pattern from cpc and appointment booking config
//  * @returns boolean
//*
export const isThereConfigForFormId = ({ config, formId }) =>
  !!getEnvConfigForFormId({ formId, config });

/**
 * setTransitionStyling
 * @param {direction} string i.e "left" | "right"
 * @param {transitionDistance} number the distance that the transitioning component will travel on enter and exit
 *
 * The styles of the child container of the Transition are dependent on its mounting state (entering / exiting)
 * and the navigation direction which is being applied to it i.e. it will have left and out and right and in when
 * appearing from the right. Thus it is dynamically styled.
 *
 * This creates a race condition between the styles of the Transition component and the NEW direction prop, which
 * will ONLY BE DECLARED AFTER THE EXIT transition/unmounting style has been applied to the component, so we need
 * to set the style again after it has been mounted with styles that are appropriate for it unmounting - given the
 * direction.
 *
 * To do this setTransitionStyling is called in the navigation / page change command (i.e. before the transition)
 * to determine left/right styles and overwrite the styling to the direction required.
 */
export const setTransitionStyling = ({
  direction,
  withEnter, // Dynamic Height has a custom Enter animation flow
  transitionDistance = 668,
  isBounceNavigationAnimationEnabled,
}) => {
  if (isBounceNavigationAnimationEnabled) {
    /**
     * Dynamic Height, CPC cards and appointment booking all have a custom Enter animation flow (i.e. withEnter = false)
     */
    let transitionStyles;
    if (direction === 'left') {
      // i.e. navigating forward bounceInUp and bounceOutUp
      const responsiveKeyframesStyles = withEnter
        ? bounceOutUpCss({ transitionDistance }) +
          bounceInUpCss({
            transitionDistance,
            includeIdAnimationMapping: false,
          })
        : bounceOutUpCss({ transitionDistance });
      transitionStyles = responsiveKeyframesStyles + forwardDirectionBounceCSS;
    } else {
      // i.e. navigating back bounceInDown and bounceOutDown
      const responsiveKeyframesStyles = withEnter
        ? bounceOutDownCss({ transitionDistance }) +
          bounceInDownCss({
            transitionDistance,
            includeIdAnimationMapping: false,
          })
        : bounceOutDownCss({ transitionDistance });
      transitionStyles = responsiveKeyframesStyles + backwardDirectionBounceCSS;
    }
    applyStyleSheet({
      stylesToApply: transitionStyles,
      styleSheetId: 'transition-style',
    });
  } else {
    const responsiveExitStyles = `
    @keyframes fadeOutRightBig {
      0% {
        opacity: 1;
      }
      100% {
        opacity: 0;
        transform: translate3d(${transitionDistance}px, 0, 0);
      }
    }
    @keyframes fadeOutLeftBig {
      0% {
        opacity: 1;
      }
      100% {
        opacity: 0;
        transform: translate3d(-${transitionDistance}px, 0, 0);
      }
    }
  `;

    const responsiveEnterStyles = `
      @keyframes fadeInRightBig {
        0% {
          opacity: 0;
          transform: translate3d(${transitionDistance}px, 0, 0);
        }
        100% {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }
      }
       @keyframes fadeInLeftBig {
         0% {
           opacity: 0;
           transform: translate3d(-${transitionDistance}px, 0, 0);
         }
         100% {
           opacity: 1;
           transform: translate3d(0, 0, 0);
         }
       }
    `;

    const leftDirectionCSS = `
      .transition-appointment-booking-direction-enter-active {
        animation-name: fadeInRightBig;
      }
      .transition-appointment-booking-direction-exit-active {
        animation-name: fadeOutLeftBig;
      }
      .transition-cpc-card-direction-enter-active {
        animation-name: fadeInRightBig;
      }
      .transition-cpc-card-direction-exit-active {
        animation-name: fadeOutLeftBig;
      }
      .transition-direction-enter-active {
        animation-name: fadeInRightBig;
      }
      .transition-direction-exit-active {
        animation-name: fadeOutLeftBig;
      }
    `;

    const rightDirectionCSS = `
      .transition-appointment-booking-direction-enter-active {
        animation-name: fadeInLeftBig;
      }
      .transition-appointment-booking-direction-exit-active {
        animation-name: fadeOutRightBig;
      }
      .transition-cpc-card-direction-enter-active {
        animation-name: fadeInLeftBig;
      }
      .transition-cpc-card-direction-exit-active {
        animation-name: fadeOutRightBig;
      }
      .transition-direction-enter-active {
        animation-name: fadeInLeftBig;
      }
      .transition-direction-exit-active {
        animation-name: fadeOutRightBig;
      }
    `;

    /**
     * Dynamic Height, CPC cards and appointment booking all have a custom Enter animation flow (i.e. withEnter = false)
     */
    const responsiveKeyframesStyles = withEnter
      ? responsiveExitStyles + responsiveEnterStyles
      : responsiveExitStyles;

    let transitionDirectionStyle;

    if (direction === 'left') {
      transitionDirectionStyle = responsiveKeyframesStyles + leftDirectionCSS;
    } else {
      transitionDirectionStyle = responsiveKeyframesStyles + rightDirectionCSS;
    }
    applyStyleSheet({
      stylesToApply: transitionDirectionStyle,
      styleSheetId: 'transition-style',
    });
  }
};

/**
 * prepareTransitionContainerForSynchronisedAnimation
 *
 * @param {theme} object
 * @param {direction} string i.e. 'vertical' | 'horizontal'
 *
 * This function responsively calculates the transition distance that the
 * navigation related animation needs to cover.
 */
export const calculateTransitionDistance = ({
  theme,
  direction = 'horizontal',
}) => {
  if (direction === 'vertical') {
    return document.body.clientHeight * 1.25;
  }
  const { tabletSmall, md, desktopSmall } = theme.breakpoints.values;
  let distance = md;

  if (document.body.clientWidth >= desktopSmall) {
    distance = 1000;
  }

  if (document.body.clientWidth < tabletSmall) {
    distance = 400;
  }

  return distance;
};

/**
 * prepareTransitionContainerForSynchronisedAnimation
 *
 * @param {direction} string i.e. 'left' | 'right'
 *
 * This function only runs if either dynamicHeight is enabled or a cpc card form,
 * or an appointment booking form, or a newsletter signup form are in use
 *
 * It temporarily updates the transitionContainer's styles in preparation for an
 * ENTER animation of the inputArea (or cpcCardContainer or newsLetterSignupPage or appointmentBooking page)
 * from either the left or right. This is in order to shift the horizontal starting
 * position of the soon-to-mount inputArea off the viewport to the side of the widget.
 * A post-transition event listener also gets added ensuring that this style change
 * gets reverted as soon as the synchronised enter animation is done.
 */
export const prepareTransitionContainerForSynchronisedAnimation = ({
  direction,
  theme,
  mode,
  dispatch,
  widgetResizeDuration,
  isBounceNavigationAnimationEnabled,
}) => {
  /**
   * The activateSynchronisedEnterAnimation function does not always get called
   * from components. Therefore dispatch cannot be passed in easily.
   * This reference puts dispatch in scope.
   */
  if (
    !window.__private__ ||
    typeof window.__private__ !== 'object' ||
    Array.isArray(window.__private__)
  ) {
    window.__private__ = {};
  }
  window.__private__.dispatchFunctionRef = dispatch;
  let transitionContainer;
  if (mode.transitionContainer === true) {
    const containers = document.querySelectorAll('.transitionContainer');

    transitionContainer =
      containers && containers.length === 1 ? containers[0] : undefined;
  }

  let cpcCardTransitionContainer;
  if (mode.cpcCardTransitionContainer === true) {
    const containersCpcCard = document.querySelectorAll(
      '.cpcCardTransitionContainer'
    );

    cpcCardTransitionContainer =
      containersCpcCard && containersCpcCard.length === 1
        ? containersCpcCard[0]
        : undefined;
  }

  let appointmentBookingTransitionContainer;
  if (mode.appointmentBookingTransitionContainer === true) {
    const containersAppointmentBooking = document.querySelectorAll(
      '.appointmentBookingTransitionContainer'
    );

    appointmentBookingTransitionContainer =
      containersAppointmentBooking && containersAppointmentBooking.length === 1
        ? containersAppointmentBooking[0]
        : undefined;
  }

  let newsletterSignupTransitionContainer;
  if (mode.newsletterSignupTransitionContainer === true) {
    const newsLetterSignupContainers = document.querySelectorAll(
      '.newsletterSignupTransitionContainer'
    );
    newsletterSignupTransitionContainer =
      newsLetterSignupContainers && newsLetterSignupContainers.length === 1
        ? newsLetterSignupContainers[0]
        : undefined;
  }

  const transitionDistance = calculateTransitionDistance({
    theme,
    direction: isBounceNavigationAnimationEnabled ? 'vertical' : 'horizontal',
  });

  setTransitionStyling({
    direction,
    withEnter: false,
    transitionDistance,
    isBounceNavigationAnimationEnabled,
  });

  let synchronisedEnterAnimationStyles = '';

  let horizontalStartingPosition;
  let verticalStartingPosition;
  if (mode.transitionContainer === true) {
    if (transitionContainer) {
      /**
       * To ensure there is no flicker
       * - when isBounceNavigationAnimationEnabled = false:
       * Place the transition container off screen, to the left or right of the
       * widget, with some extra pixels (until the synchronised transition is
       * ready to begin)
       * - when isBounceNavigationAnimationEnabled = true:
       * Hide the transition container with a 0% opacity (until the synchronised
       * transition is ready to begin)
       * Also place the transition container off screen, to the bottom or top of the
       * widget, with some extra pixels (until the synchronised transition is
       * ready to begin)
       */
      horizontalStartingPosition =
        direction === 'right'
          ? `calc(${transitionContainer.clientWidth * 1.25}px)`
          : `calc(${transitionContainer.clientWidth * -1.25}px)`;
      verticalStartingPosition =
        direction === 'right'
          ? `calc(${transitionContainer.clientHeight * 1.25}px)`
          : `calc(${transitionContainer.clientHeight * -1.25}px)`;

      transitionContainer.style.animationDuration = `${widgetResizeDuration}ms`;
      if (isBounceNavigationAnimationEnabled) {
        transitionContainer.style.opacity = 0;
        transitionContainer.style.top = verticalStartingPosition;
      } else {
        transitionContainer.style.position = 'relative'; // gets set to 'relative' in the beginning of every navigation cycle (i.e. EXIT+ENTER phase) and RESETs back to 'static' after the enter animation completes
        transitionContainer.style.right = horizontalStartingPosition;
      }
      if (isBounceNavigationAnimationEnabled) {
        synchronisedEnterAnimationStyles +=
          direction === 'right'
            ? bounceInUpCss({
                transitionDistance,
                transitionContainerId: '#synchronisedEnterContainer',
              })
            : bounceInDownCss({
                transitionDistance,
                transitionContainerId: '#synchronisedEnterContainer',
              });
      } else {
        horizontalStartingPosition =
          direction === 'right'
            ? `calc(${transitionContainer.clientWidth * 1.25}px)`
            : `calc(${transitionContainer.clientWidth * -1.25}px)`;

        transitionContainer.style.animationDuration = `${widgetResizeDuration}ms`;
        transitionContainer.style.position = 'relative'; // gets set to 'relative' in the beginning of every navigation cycle (i.e. EXIT+ENTER phase) and RESETs back to 'static' after the enter animation completes
        transitionContainer.style.right = horizontalStartingPosition;
        const horizontalShiftStyleMap = {
          initial: `right: ${horizontalStartingPosition};`,
          final: 'right: 0%;',
        };

        synchronisedEnterAnimationStyles += `
          @keyframes synchronisedEnterFadeIn {
            0% {
              opacity: 0;
              ${horizontalShiftStyleMap.initial}
            }
            100% {
              opacity: 1;
              ${horizontalShiftStyleMap.final}
            }
          }

          #synchronisedEnterContainer {
            animation-name: synchronisedEnterFadeIn;
          }
        `;
      }
    }
  }

  let horizontalStartingPositionCpcCard = '';
  let verticalStartingPositionCpcCard = '';
  if (mode.cpcCardTransitionContainer === true) {
    if (cpcCardTransitionContainer) {
      /**
       * To ensure there is no flicker
       * - when isBounceNavigationAnimationEnabled = false:
       * Place the transition container off screen, to the left or right of the
       * widget, with some extra pixels (until the synchronised transition is
       * ready to begin)
       * - when isBounceNavigationAnimationEnabled = true:
       * Hide the transition container with a 0% opacity (until the synchronised
       * transition is ready to begin)
       * Also place the transition container off screen, to the bottom or top of the
       * widget, with some extra pixels (until the synchronised transition is
       * ready to begin)
       */
      horizontalStartingPositionCpcCard =
        direction === 'right'
          ? `calc(${cpcCardTransitionContainer.clientWidth * 1.25}px)`
          : `calc(${cpcCardTransitionContainer.clientWidth * -1.25}px)`;
      verticalStartingPositionCpcCard =
        direction === 'right'
          ? `calc(${cpcCardTransitionContainer.clientHeight * 1.25}px)`
          : `calc(${cpcCardTransitionContainer.clientHeight * -1.25}px)`;

      cpcCardTransitionContainer.style.animationDuration = `${widgetResizeDuration}ms`;
      if (isBounceNavigationAnimationEnabled) {
        cpcCardTransitionContainer.style.opacity = 0;
        cpcCardTransitionContainer.style.top = verticalStartingPositionCpcCard;
      } else {
        cpcCardTransitionContainer.style.position = 'relative'; // gets set to 'relative' in the beginning of every navigation cycle (i.e. EXIT+ENTER phase) and RESETs back to 'static' after the enter animation completes
        cpcCardTransitionContainer.style.right =
          horizontalStartingPositionCpcCard;
      }

      if (isBounceNavigationAnimationEnabled) {
        synchronisedEnterAnimationStyles +=
          direction === 'right'
            ? bounceInUpCss({
                transitionDistance,
                transitionContainerId: '#synchronisedEnterContainerCpc',
              })
            : bounceInDownCss({
                transitionDistance,
                transitionContainerId: '#synchronisedEnterContainerCpc',
              });
      } else {
        horizontalStartingPositionCpcCard =
          direction === 'right'
            ? `calc(${cpcCardTransitionContainer.clientWidth * 1.25}px)`
            : `calc(${cpcCardTransitionContainer.clientWidth * -1.25}px)`;

        cpcCardTransitionContainer.style.animationDuration = `${widgetResizeDuration}ms`;
        cpcCardTransitionContainer.style.position = 'relative'; // gets set to 'relative' in the beginning of every navigation cycle (i.e. EXIT+ENTER phase) and RESETs back to 'static' after the enter animation completes
        cpcCardTransitionContainer.style.right =
          horizontalStartingPositionCpcCard;
        const horizontalShiftStyleMapForCpc = {
          initial: `right: ${horizontalStartingPositionCpcCard};`,
          final: 'right: 0%;',
        };

        synchronisedEnterAnimationStyles += `
          @keyframes synchronisedEnterFadeInCpcCard {
            0% {
              opacity: 0;
              ${horizontalShiftStyleMapForCpc.initial}
            }
            100% {
              opacity: 1;
              ${horizontalShiftStyleMapForCpc.final}
            }
          }

          #synchronisedEnterContainerCpc {
            animation-name: synchronisedEnterFadeInCpcCard;
          }
        `;
      }
    }
  }

  let horizontalStartingPositionAppointmentBooking = '';
  const verticalStartingPositionAppointmentBooking = '';
  if (mode.appointmentBookingTransitionContainer === true) {
    if (appointmentBookingTransitionContainer) {
      /**
       * To ensure there is no flicker
       * - when isBounceNavigationAnimationEnabled = false:
       * Place the transition container off screen, to the left or right of the
       * widget, with some extra pixels (until the synchronised transition is
       * ready to begin)
       * - when isBounceNavigationAnimationEnabled = true:
       * Hide the transition container with a 0% opacity (until the synchronised
       * transition is ready to begin)
       * Also place the transition container off screen, to the bottom or top of the
       * widget, with some extra pixels (until the synchronised transition is
       * ready to begin)
       */
      horizontalStartingPositionAppointmentBooking =
        direction === 'right'
          ? `calc(${
              appointmentBookingTransitionContainer.clientWidth * 1.25
            }px)`
          : `calc(${
              appointmentBookingTransitionContainer.clientWidth * -1.25
            }px)`;
      verticalStartingPositionCpcCard =
        direction === 'right'
          ? `calc(${
              appointmentBookingTransitionContainer.clientHeight * 1.25
            }px)`
          : `calc(${
              appointmentBookingTransitionContainer.clientHeight * -1.25
            }px)`;

      appointmentBookingTransitionContainer.style.animationDuration = `${widgetResizeDuration}ms`;
      if (isBounceNavigationAnimationEnabled) {
        appointmentBookingTransitionContainer.style.opacity = 0;
        appointmentBookingTransitionContainer.style.top =
          verticalStartingPositionAppointmentBooking;
      } else {
        appointmentBookingTransitionContainer.style.position = 'relative'; // gets set to 'relative' in the beginning of every navigation cycle (i.e. EXIT+ENTER phase) and RESETs back to 'static' after the enter animation completes
        appointmentBookingTransitionContainer.style.right =
          horizontalStartingPositionAppointmentBooking;
      }

      if (isBounceNavigationAnimationEnabled) {
        synchronisedEnterAnimationStyles +=
          direction === 'right'
            ? bounceInUpCss({
                transitionDistance,
                transitionContainerId:
                  '#synchronisedEnterContainerAppointmentBooking',
              })
            : bounceInDownCss({
                transitionDistance,
                transitionContainerId:
                  '#synchronisedEnterContainerAppointmentBooking',
              });
      } else {
        horizontalStartingPositionAppointmentBooking =
          direction === 'right'
            ? `calc(${
                appointmentBookingTransitionContainer.clientWidth * 1.25
              }px)`
            : `calc(${
                appointmentBookingTransitionContainer.clientWidth * -1.25
              }px)`;

        appointmentBookingTransitionContainer.style.animationDuration = `${widgetResizeDuration}ms`;
        appointmentBookingTransitionContainer.style.position = 'relative'; // gets set to 'relative' in the beginning of every navigation cycle (i.e. EXIT+ENTER phase) and RESETs back to 'static' after the enter animation completes
        appointmentBookingTransitionContainer.style.right =
          horizontalStartingPositionAppointmentBooking;
        const horizontalShiftStyleMapForAb = {
          initial: `right: ${horizontalStartingPositionAppointmentBooking};`,
          final: 'right: 0%;',
        };

        synchronisedEnterAnimationStyles += `
          @keyframes synchronisedEnterFadeInAppointmentBooking {
            0% {
              opacity: 0;
              ${horizontalShiftStyleMapForAb.initial}
            }
            100% {
              opacity: 1;
              ${horizontalShiftStyleMapForAb.final}
            }
          }

          #synchronisedEnterContainerAppointmentBooking {
            animation-name: synchronisedEnterFadeInAppointmentBooking;
          }
        `;
      }
    }
  }

  let horizontalStartingPositionNewsLetterSignupPage = '';
  let verticalStartingPositionNewsLetterSignupPage = '';
  if (mode.newsletterSignupTransitionContainer === true) {
    if (newsletterSignupTransitionContainer) {
      /**
       * To ensure there is no flicker
       * - when isBounceNavigationAnimationEnabled = false:
       * Place the transition container off screen, to the left or right of the
       * widget, with some extra pixels (until the synchronised transition is
       * ready to begin)
       * - when isBounceNavigationAnimationEnabled = true:
       * Hide the transition container with a 0% opacity (until the synchronised
       * transition is ready to begin)
       * Also place the transition container off screen, to the bottom or top of the
       * widget, with some extra pixels (until the synchronised transition is
       * ready to begin)
       */
      horizontalStartingPositionNewsLetterSignupPage =
        direction === 'right'
          ? `calc(${newsletterSignupTransitionContainer.clientWidth * 1.25}px)`
          : `calc(${
              newsletterSignupTransitionContainer.clientWidth * -1.25
            }px)`;
      verticalStartingPositionNewsLetterSignupPage =
        direction === 'right'
          ? `calc(${newsletterSignupTransitionContainer.clientHeight * 1.25}px)`
          : `calc(${
              newsletterSignupTransitionContainer.clientHeight * -1.25
            }px)`;

      newsletterSignupTransitionContainer.style.animationDuration = `${widgetResizeDuration}ms`;
      if (isBounceNavigationAnimationEnabled) {
        newsletterSignupTransitionContainer.style.opacity = 0;
        newsletterSignupTransitionContainer.style.top =
          verticalStartingPositionNewsLetterSignupPage;
      } else {
        newsletterSignupTransitionContainer.style.position = 'relative'; // gets set to 'relative' in the beginning of every navigation cycle (i.e. EXIT+ENTER phase) and RESETs back to 'static' after the enter animation completes
        newsletterSignupTransitionContainer.style.right =
          horizontalStartingPositionNewsLetterSignupPage;
      }

      if (isBounceNavigationAnimationEnabled) {
        synchronisedEnterAnimationStyles +=
          direction === 'right'
            ? bounceInUpCss({
                transitionDistance,
                transitionContainerId: '#synchronisedEnterContainerNews',
              })
            : bounceInDownCss({
                transitionDistance,
                transitionContainerId: '#synchronisedEnterContainerNews',
              });
      } else {
        horizontalStartingPositionNewsLetterSignupPage =
          direction === 'right'
            ? `calc(${
                newsletterSignupTransitionContainer.clientWidth * 1.25
              }px)`
            : `calc(${
                newsletterSignupTransitionContainer.clientWidth * -1.25
              }px)`;
        newsletterSignupTransitionContainer.style.animationDuration = `${widgetResizeDuration}ms`;
        newsletterSignupTransitionContainer.style.position = 'relative'; // gets set to 'relative' in the beginning of every navigation cycle (i.e. EXIT+ENTER phase) and RESETs back to 'static' after the enter animation completes
        newsletterSignupTransitionContainer.style.right =
          horizontalStartingPositionNewsLetterSignupPage;
        const horizontalShiftStyleMapForNs = {
          initial: `right: ${horizontalStartingPositionNewsLetterSignupPage};`,
          final: 'right: 0%;',
        };
        newsletterSignupTransitionContainer.style.opacity = 1;

        synchronisedEnterAnimationStyles += `
          @keyframes synchronisedEnterFadeInNewsLetterSignup {
            0% {
              opacity: 0;
              ${horizontalShiftStyleMapForNs.initial}
            }
            100% {
              opacity: 1;
              ${horizontalShiftStyleMapForNs.final}
            }
          }

          #synchronisedEnterContainerNews {
            animation-name: synchronisedEnterFadeInNewsLetterSignup;
          }
        `;
      }
    }
  }

  /**
   * Shift the transition container off screen in anticipation of it being
   * animated back in - in cases where either dynamicHeight is enabled or
   * if it is a cpc card form, an appointment booking form, or a newsletter
   * signup form.
   */
  applyStyleSheet({
    stylesToApply: synchronisedEnterAnimationStyles,
    styleSheetId: 'synchronised-enter-animation-style',
  });

  if (
    !window.__private__ ||
    typeof window.__private__ !== 'object' ||
    Array.isArray(window.__private__)
  ) {
    window.__private__ = {};
  }
  window.__private__.hasEnterAnimationRunAlreadyInThisNavigationCycle = false;
  window.__private__.isSynchronisedEnterAnimationReady = true;
};

/**
 * prepareFormTransitionContainerForCustomExitAnimation
 *
 * @param {theme} object
 *
 * This function runs as soon as a form submission has been initiated
 *
 * This function temporarily updates the formTransitionContainer's styles in
 * preparation for an EXIT animation of the entire SubmissionPage either to the
 * left of the screen (if isBounceNavigationAnimationEnabled is falsy) or
 * both fading out and bouncing out (if isBounceNavigationAnimationEnabled is true).
 * A post-transition event listener also gets added ensuring that this style change
 * gets reverted as soon as the synchronised enter animation is done.
 */
export const prepareFormTransitionContainerForCustomExitAnimation = ({
  theme,
  widgetResizeDuration,
  isBounceNavigationAnimationEnabled,
}) => {
  const containers = document.querySelectorAll('.formTransitionContainer');
  const formTransitionContainer =
    containers && containers.length === 1 ? containers[0] : undefined;

  if (formTransitionContainer) {
    const transitionDistance = calculateTransitionDistance({
      theme,
      direction: isBounceNavigationAnimationEnabled ? 'vertical' : 'horizontal',
    });

    setTransitionStyling({
      direction: 'left',
      withEnter: false,
      transitionDistance,
      isBounceNavigationAnimationEnabled,
    });

    formTransitionContainer.style.animationDuration = `${
      widgetResizeDuration || TRANSITION_TIMEOUT_TIME + 100
    }ms`;
    let customExitAnimationStyles;

    if (isBounceNavigationAnimationEnabled) {
      customExitAnimationStyles = customExitBounceOutUpCss({
        transitionDistance,
        transitionContainerId: '#customExitContainer',
      });
    } else {
      /**
       * Set the final transition container position, off-screen to the left of
       * the widget, with some extra for a clean snappy transition
       */
      const horizontalEndPosition = `calc(${transitionDistance * 1.25}px)`;

      formTransitionContainer.style.position = 'relative'; // gets set to 'relative' in the beginning of the submission attempt and RESETs back to 'static' after the custom exit animation completes
      formTransitionContainer.style.right = '0%';

      customExitAnimationStyles = `
        @keyframes customExitFadeOut {
          0% {
            opacity: 1;
            right: 0%;
          }
          100% {
            opacity: 0;
            right: ${horizontalEndPosition};
          }
        }

        #customExitContainer {
          animation-name: customExitFadeOut;
        }
      `;
    }
    applyStyleSheet({
      stylesToApply: customExitAnimationStyles,
      styleSheetId: 'custom-exit-animation-style',
    });
  }
};

/**
 * resetFormTransitionContainerStyles
 *
 * This function runs in one of two cases:
 *
 * - either if an attempted form submission has failed
 * - or after the custom exit transition has completed and the
 *   SubmissionPage has unmounted
 *
 * It restores the original styling of the now stationary container element
 */
export const resetFormTransitionContainerStyles = ({
  isBounceNavigationAnimationEnabled,
}) => {
  const containers = document.querySelectorAll('.formTransitionContainer');
  const formTransitionContainer =
    containers && containers.length === 1 ? containers[0] : undefined;

  if (formTransitionContainer) {
    if (isBounceNavigationAnimationEnabled) {
      formTransitionContainer.style.opacity = 1;
    }
    formTransitionContainer.style.position = 'static';
    formTransitionContainer.style.animationDuration = '0s';
    formTransitionContainer.id = '';
  }
};

/**
 * activateSynchronisedEnterAnimation
 *
 * @param {direction} string i.e. 'left' | 'right'
 *
 * This function only runs if either dynamicHeight is enabled or if it is a cpc
 * card form, an appointment booking form, or a newsletter signup form.
 *
 * It kicks off an ENTER animation of the inputArea from either the left or right
 * and is fully synchronised in its duration with the dynamic height resize of the widget.
 * The synchronisation is accomplished by setting the animationDuration here to match the value
 * that the resize transition of the widget is configured to within the formLoader.
 * To change the value, search for 'variableHeightTransitionStyle' in the formLoader.
 *
 * This function can be called from five entry points, either:
 * ... when dynamicHeight is enabled &
 * - when a widget height resize needs to happen
 * - or after widget height calculations for the new page are completed
 * ... or irrespective of whether dynamicHeight is enabled or not &
 * - navigation has been validly inititated and the next page contains CPC click out cards
 * - navigation has been validly inititated and the next page contains an appointment booking embed
 * - submission has been successfully performed and the next page is a newsletter signup page
 */
export const activateSynchronisedEnterAnimation = ({
  widgetResizeDuration,
  mode,
  isBounceNavigationAnimationEnabled,
  verticalTransitionContainerOffset = '25px',
}) => {
  let transitionContainer;
  if (mode.transitionContainer === true) {
    const containers = document.querySelectorAll('.transitionContainer');
    transitionContainer =
      containers && containers.length === 1 ? containers[0] : undefined;
  }

  let cpcCardTransitionContainer;
  if (mode.cpcCardTransitionContainer === true) {
    const cpcCardContainers = document.querySelectorAll(
      '.cpcCardTransitionContainer'
    );
    cpcCardTransitionContainer =
      cpcCardContainers && cpcCardContainers.length === 1
        ? cpcCardContainers[0]
        : undefined;
  }

  /**
   * get the first appointmentBooking container
   */
  let appointmentBookingTransitionContainer;
  if (mode.appointmentBookingTransitionContainer === true) {
    const appointmentBookingContainers = document.querySelectorAll(
      '.appointmentBookingTransitionContainer'
    );
    appointmentBookingTransitionContainer =
      appointmentBookingContainers && appointmentBookingContainers.length === 1
        ? appointmentBookingContainers[0]
        : undefined;
  }

  let newsletterSignupTransitionContainer;
  if (mode.newsletterSignupTransitionContainer === true) {
    const newsLetterSignupContainers = document.querySelectorAll(
      '.newsletterSignupTransitionContainer'
    );
    newsletterSignupTransitionContainer =
      newsLetterSignupContainers && newsLetterSignupContainers.length === 1
        ? newsLetterSignupContainers[0]
        : undefined;
  }
  const hasEnterAnimationRunAlreadyInThisNavigationCycle =
    window.__private__?.hasEnterAnimationRunAlreadyInThisNavigationCycle;

  const isContainerSynchronisedEnterAnimationReady =
    window.__private__?.isSynchronisedEnterAnimationReady;

  if (
    !isContainerSynchronisedEnterAnimationReady ||
    hasEnterAnimationRunAlreadyInThisNavigationCycle
  ) {
    /* The conditional 'hasEnterAnimationRunAlreadyInThisNavigationCycle' guards against running the enter animation more than once within a nav cycle
     * When isDynamicHeightEnabled is true, there are two possible entry points into this function:
     * - when a widget height resize needs to happen
     * as it can be called as a side-effect by the dynamicHeight slice's reducer if and ONLY IF there is an impending widget resize in this nav cycle
     * - OR after widget height calculations for the new page are completed
     * i.e. this function can be called by <Transition/>'s useEffect after the dynamicHeight-slice value isWidgetHeightRecalculationRequired toggles to false.
     */
    return;
  }

  if (transitionContainer && mode.transitionContainer === true) {
    // Setting the target/final style property values that the impending ENTER animation will achieve
    if (isBounceNavigationAnimationEnabled) {
      transitionContainer.style.opacity = 1;
      transitionContainer.style.top = verticalTransitionContainerOffset;
    } else {
      transitionContainer.style.right = '0%';
    }
    transitionContainer.style.animationDuration = `${widgetResizeDuration}ms`; // NOTE: Keep this value in sync with 'variableHeightTransitionStyle' as explained by the top-level comment above this function
  }

  if (cpcCardTransitionContainer && mode.cpcCardTransitionContainer === true) {
    // Setting the target/final style property values that the impending ENTER animation will achieve
    if (isBounceNavigationAnimationEnabled) {
      cpcCardTransitionContainer.style.opacity = 1;
      cpcCardTransitionContainer.style.top = verticalTransitionContainerOffset;
    } else {
      cpcCardTransitionContainer.style.right = '0%';
    }
    cpcCardTransitionContainer.style.animationDuration = `${widgetResizeDuration}ms`; // NOTE: Keep this value in sync with 'variableHeightTransitionStyle' as explained by the top-level comment above this function
  }

  if (
    appointmentBookingTransitionContainer &&
    mode.appointmentBookingTransitionContainer === true
  ) {
    // Setting the target/final style property values that the impending ENTER animation will achieve
    if (isBounceNavigationAnimationEnabled) {
      appointmentBookingTransitionContainer.style.opacity = 1;
      appointmentBookingTransitionContainer.style.top =
        verticalTransitionContainerOffset;
    } else {
      appointmentBookingTransitionContainer.style.right = '0%';
    }
    appointmentBookingTransitionContainer.style.animationDuration = `${widgetResizeDuration}ms`; // NOTE: Keep this value in sync with 'variableHeightTransitionStyle' as explained by the top-level comment above this function
  }

  if (
    newsletterSignupTransitionContainer &&
    mode.newsletterSignupTransitionContainer === true
  ) {
    // Setting the target/final style property values that the impending ENTER animation will achieve
    if (isBounceNavigationAnimationEnabled) {
      newsletterSignupTransitionContainer.style.opacity = 1;
      newsletterSignupTransitionContainer.style.top =
        verticalTransitionContainerOffset;
    } else {
      newsletterSignupTransitionContainer.style.right = '0%';
    }
    newsletterSignupTransitionContainer.style.animationDuration = `${widgetResizeDuration}ms`; // NOTE: Keep this value in sync with 'variableHeightTransitionStyle' as explained by the top-level comment above this function
  }

  if (mode.transitionContainer === true) {
    transitionContainer.id = 'synchronisedEnterContainer';
  }

  if (mode.cpcCardTransitionContainer === true) {
    cpcCardTransitionContainer.id = 'synchronisedEnterContainerCpc';
  }

  if (mode.appointmentBookingTransitionContainer === true) {
    appointmentBookingTransitionContainer.id =
      'synchronisedEnterContainerAppointmentBooking';
  }

  if (mode.newsletterSignupTransitionContainer === true) {
    const nextContainer = document.getElementById('__next');
    if (nextContainer) {
      if (mode.isDynamicHeightEnabled) {
        nextContainer.style.overflow = 'hidden';
      } else {
        nextContainer.style.overflowX = 'hidden';
      }
    }
    newsletterSignupTransitionContainer.id = 'synchronisedEnterContainerNews';
  }

  window.__private__.isEnterAnimationInProgress = true;

  const firstContainer =
    transitionContainer ||
    cpcCardTransitionContainer ||
    appointmentBookingTransitionContainer ||
    newsletterSignupTransitionContainer;

  // TODO extract - turn into a helper
  const resetStylesFunction = () => {
    setTimeout(() => {
      if (transitionContainer && mode.transitionContainer === true) {
        // Reset styles back to the initial transitionContainer settings
        if (isBounceNavigationAnimationEnabled) {
          transitionContainer.style.opacity = 1;
        }
        transitionContainer.style.position = 'static';
        transitionContainer.style.animationDuration = `${
          widgetResizeDuration || TRANSITION_TIMEOUT_TIME + 100
        }ms`;
        transitionContainer.id = '';
      }

      if (
        cpcCardTransitionContainer &&
        mode.cpcCardTransitionContainer === true
      ) {
        // Reset styles back to the initial settings
        if (isBounceNavigationAnimationEnabled) {
          cpcCardTransitionContainer.style.opacity = 1;
        }
        cpcCardTransitionContainer.style.position = 'static';
        cpcCardTransitionContainer.style.animationDuration = `${
          widgetResizeDuration || TRANSITION_TIMEOUT_TIME + 100
        }ms`;
        cpcCardTransitionContainer.id = '';
      }

      if (
        appointmentBookingTransitionContainer &&
        mode.appointmentBookingTransitionContainer === true
      ) {
        // Reset styles back to the initial settings
        if (isBounceNavigationAnimationEnabled) {
          appointmentBookingTransitionContainer.style.opacity = 1;
        }
        appointmentBookingTransitionContainer.style.position = 'static';
        appointmentBookingTransitionContainer.style.animationDuration = `${
          widgetResizeDuration || TRANSITION_TIMEOUT_TIME + 100
        }ms`;
        appointmentBookingTransitionContainer.id = '';
      }

      if (
        newsletterSignupTransitionContainer &&
        mode.newsletterSignupTransitionContainer === true
      ) {
        // Reset styles back to the initial newsletterSignupTransitionContainer settings
        if (isBounceNavigationAnimationEnabled) {
          newsletterSignupTransitionContainer.style.opacity = 1;
        }
        newsletterSignupTransitionContainer.style.position = 'static';
        newsletterSignupTransitionContainer.style.animationDuration = `${
          widgetResizeDuration || TRANSITION_TIMEOUT_TIME + 100
        }ms`;

        newsletterSignupTransitionContainer.id = '';
        setTimeout(() => {
          // This setTimeout prevents a brief vertical scroll bar flicker whilst the height resize has not completed (when custom transitions are required)
          const nextContainer = document.getElementById('__next');
          if (nextContainer) {
            if (mode.isDynamicHeightEnabled) {
              nextContainer.style.overflow = 'visible';
            } else {
              nextContainer.style.overflowX = 'visible';
            }
          }
        }, 1000);
      }
      window.__private__.isPageTransitionInProgress = false;
      window.__private__.isEnterAnimationInProgress = false;
      window.__private__.dispatchFunctionRef(
        updateEnterPhase({
          enterPhase: false,
        })
      );
      window.__private__.isSynchronisedEnterAnimationReady = false;
      // TODO assumption that there is a container - need a guard clause
    }, parseInt(firstContainer.style.animationDuration, 10));
  };
  window.__private__.hasEnterAnimationRunAlreadyInThisNavigationCycle = true;
  resetStylesFunction();
};

/**
 * activateCustomExitAnimationOfFormContainer
 *
 * @param {widgetResizeDuration} number
 * @param {theme} object
 * @param {animationCompletedCallback} function i.e. dispatches a notification to the store informing the app that the Submission page may now be unmounted
 *
 * This function transitions the formTransitionContainer out of the viewport once the form submission has completed
 *
 */
export const activateCustomExitAnimationOfFormContainer = ({
  widgetResizeDuration,
  theme,
  animationCompletedCallback,
  isBounceNavigationAnimationEnabled,
}) => {
  const formTransitionContainer = document.querySelector(
    '.formTransitionContainer'
  );
  if (formTransitionContainer) {
    const transitionDistance = calculateTransitionDistance({
      theme,
      direction: isBounceNavigationAnimationEnabled ? 'vertical' : 'horizontal',
    });
    if (isBounceNavigationAnimationEnabled) {
      const verticalEndPosition = `calc(${transitionDistance * 1.25}px)`;
      formTransitionContainer.style.top = verticalEndPosition;
    } else {
      const horizontalEndPosition = `calc(${transitionDistance * 1.25}px)`;
      formTransitionContainer.style.right = horizontalEndPosition;
    }
    formTransitionContainer.style.animationDuration = `${widgetResizeDuration}ms`; // NOTE: Keep this value in sync with 'variableHeightTransitionStyle' as explained by the top-level comment above this function
    formTransitionContainer.id = 'customExitContainer';

    setTimeout(animationCompletedCallback, widgetResizeDuration);
  }
};
